import { startOfDay } from 'date-fns';
import store from '@/store';
import {
  UserTaskType,
  Labels,
  LabelId,
  isRootTask,
  isTaskClosed,
} from '@/store/modules/task';
import { parseISODateString } from './date';
import { resolveTaskOwner } from '../store/modules/task';

// Timeline item types
export const TimelineItemType = {
  MESSAGE: 'message',
  USER_SIMPLE: 'user_simple',
  DATE: 'date',
  SERVICE_SIMPLE: 'service_simple',
  PATROL_SIMPLE: 'patrol_simple',
  DISPATCHER_SIMPLE: 'dispatcher_simple',
  SIMPLE: 'simple',
  AT_LOCATION: 'at_location',
};

// Message status enum
export const MessageStatus = {
  DRAFT: 'draft',
  RECEIVED: 'received',
  ERROR: 'error',
};

function resolveTimelineTaskType(task) {
  if (!task) {
    return null;
  }

  return task.task_type;
}

function formatDuration(seconds) {
  if (!seconds || seconds <= 0) return null;

  const minutes = Math.round(seconds / 60);
  if (minutes < 1) {
    return `${seconds} sek`;
  }
  if (minutes < 60) {
    return `${minutes} min`;
  }

  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}min` : `${hours}h`;
}

function serviceDurationSubtitle(task) {
  const serviceTime = task?.details?.stats?.on_site_time;
  const formatted = formatDuration(serviceTime);
  return formatted ? `Czas serwisu: ${formatted}` : '';
}

// Base timeline item class
export class TimelineItem {
  constructor(id, type, data = {}) {
    this.id = id;
    this.type = type;
    this.createdAt = data.createdAt || null;
    this.title = data.title || '';
    this.subtitle = data.subtitle || '';
    this.message = data.message || '';
    this.icon = data.icon || 'mdi-circle';
    this.color = data.color || 'default';
    this.avatar = data.avatar || false;
    this.important = data.important || false;
    this.status = data.status || MessageStatus.RECEIVED;
    this.chips = data.chips || [];
    this.payment = data.payment || null;
    this.canEdit = data.canEdit === true; // Default false, must be explicitly enabled
    this.canDelete = data.canDelete === true; // Default false, must be explicitly enabled
    this.mainAction = data.mainAction || null;
    this.mainActionTitle = data.mainActionTitle || null;
    this.source = data.source || null;
    this.taskId = data.taskId || null;
    this.taskType = data.taskType || null;
    this.user = data.user || null;
    this.isCurrentUser = data.isCurrentUser || false;
  }

  // Factory method similar to Swift's TimelineItem.create
  static createDateItem(date) {
    // Create date item based on the provided date
    return new DateItem(`date-${date.toISOString()}`, {
      title: date.toLocaleDateString(),
      createdAt: date,
    });
  }

  static create(task, status) {
    const timelineTaskType = resolveTimelineTaskType(task);

    // Create item based on labelId (similar to Swift logic)
    switch (status.label_id) {
      case LabelId.READ:
        return new UserSimpleItem(`${task.id}-${status.id}`, {
          taskId: task.id,
          title: Labels.descriptionInContext(status.label_id, timelineTaskType),
          user: resolveTaskOwner(task) || status.user || null,
          createdAt: parseISODateString(status.created_at),
          source: status,
          taskType: timelineTaskType,
          canEdit: false, // READ status should not be editable
        });

      case LabelId.COMMENT:
        var isCurrentUser = store.getters['user/isCurrentUser'](
          status.user?.id
        );
        var chips = [];
        if (isCurrentUser && timelineTaskType === UserTaskType.PATROL) {
          chips.push({ icon: 'mdi-car', name: resolveTaskOwner(task)?.name });
        } else if (isCurrentUser && timelineTaskType === UserTaskType.SERVICE) {
          chips.push({
            icon: 'mdi-wrench',
            name: resolveTaskOwner(task)?.name,
          });
        }
        return new MessageItem(`${task.id}-${status.id}`, {
          taskId: task.id,
          taskType: timelineTaskType,
          subtitle: status.message || '',
          message: status.comment || '',
          chips: chips,
          user: status.user || null,
          isCurrentUser: isCurrentUser,
          createdAt: parseISODateString(status.created_at),
          important: false,
          canEdit: true,
          canDelete: isCurrentUser, // Only current user can delete their own comments
          source: status,
        });

      case LabelId.CANCELED: {
        // CANCELED behavior depends on root vs subtask:
        // - Root task: MessageItem showing WHO CANCELED (info about dispatcher action)
        // - Subtask: PatrolSimpleItem showing WHICH PATROL (which task item is canceled)
        const isSubtask = task.parent != null
        const isPatrolTask = task.task_type === UserTaskType.PATROL
        if (isSubtask && isPatrolTask) {
          // Subtask CANCELED: Show which patrol/task is canceled (reduces visual noise)
          const canceledBy = status.user
            ? `Odwołane przez ${status.user.name}`
            : null
          return new PatrolSimpleItem(`${task.id}-${status.id}`, {
            taskId: task.id,
            title: Labels.descriptionInContext(
              status.label_id,
              timelineTaskType
            ),
            subtitle: canceledBy,
            user: resolveTaskOwner(task) || null,  // Show which patrol is assigned
            createdAt: parseISODateString(status.created_at),
            source: status,
            taskType: timelineTaskType,
            canEdit: true,
          })
        } else {
          // Root task CANCELED: Show who canceled and reason (all task types)
          return new MessageItem(`${task.id}-${status.id}`, {
            taskId: task.id,
            taskType: timelineTaskType,
            title: Labels.descriptionInContext(status.label_id, timelineTaskType),
            subtitle: status.message || 'Brak przyczyny',
            message: status.comment || '',
            createdAt: parseISODateString(status.created_at),
            user: status.user || null,  // Show who performed the cancel action
            isCurrentUser: false,
            important: true,
            source: status,
            canEdit: true,
            // Service cancellation gets a dedicated icon; other task types keep
            // the MessageItem default to avoid changing their presentation.
            icon: timelineTaskType === UserTaskType.SERVICE
              ? Labels.iconForService(status.label_id)
              : undefined,
          })
        }
      }

      case LabelId.DONE:
      case LabelId.NEGATIVE:
      case LabelId.POSITIVE: {
        // Root task or other completion statuses: MessageItem as before
        return new MessageItem(`${task.id}-${status.id}`, {
          taskId: task.id,
          taskType: timelineTaskType,
          title: Labels.descriptionInContext(status.label_id, timelineTaskType),
          subtitle: status.message || 'Brak uwag końcowych',
          message: status.comment || '',
          createdAt: parseISODateString(status.created_at),
          user: resolveTaskOwner(task) || null,
          isCurrentUser: false,
          important: true,
          source: status,
          canEdit: true,
          // Service final statuses get dedicated icons; other task types keep
          // the MessageItem default to avoid changing their presentation.
          icon: timelineTaskType === UserTaskType.SERVICE
            ? Labels.iconForService(status.label_id)
            : undefined,
        });
      }

      case LabelId.NOTIFICATION:
        return new UserSimpleItem(`${task.id}-${status.id}`, {
          taskId: task.id,
          title: Labels.descriptionInContext(status.label_id, timelineTaskType),
          user: resolveTaskOwner(task) || null,
          createdAt: parseISODateString(status.created_at),
          source: status,
          taskType: timelineTaskType,
          canEdit: true,
        });

      case LabelId.NEW:
        return new UserSimpleItem(`${task.id}-${status.id}`, {
          taskId: task.id,
          taskType: timelineTaskType,
          title: Labels.descriptionInContext(status.label_id, timelineTaskType),
          subtitle: timelineTaskType === UserTaskType.PATROL ? (task.description || '') : '',
          user: resolveTaskOwner(task) || status.user || null,
          createdAt: parseISODateString(status.created_at),
          source: status,
          canEdit: true,
        });

      case LabelId.AT_LOCATION:
        return AtLocationItem.createWithTravelTime(
          `${task.id}-${status.id}`,
          task,
          status,
          {
            canEditTravelTime: true,
            title: Labels.descriptionInContext(status.label_id, timelineTaskType),
          }
        );

      case LabelId.CLOSE: {
        const id = `${task.id}-${status.id} || ${status.created_at}`;
        const title = Labels.descriptionInContext(status.label_id, timelineTaskType);

        if (timelineTaskType === UserTaskType.SERVICE) {
          return new ServiceSimpleItem(id, {
            taskId: task.id,
            taskType: timelineTaskType,
            createdAt: parseISODateString(status.created_at),
            title,
            subtitle: serviceDurationSubtitle(task),
            icon: 'mdi-wrench',
            source: status,
            canEdit: true,
            canEditOnSiteTime: true,
          });
        }

        if (timelineTaskType === UserTaskType.PATROL) {
          return new PatrolSimpleItem(id, {
            createdAt: parseISODateString(status.created_at),
            title,
            user: resolveTaskOwner(task) || status.user || null,
            dispatcher: status.dispatcher || null,
            source: status,
          });
        }

        return new DispatcherSimpleItem(id, {
          createdAt: parseISODateString(status.created_at),
          title,
          user: resolveTaskOwner(task) || null,
          dispatcher: status.dispatcher || null,
          source: status,
        });
      }

      default: {
        const id = `${task.id}-${status.id} || ${status.created_at}`;
        const title = Labels.descriptionInContext(
          status.label_id,
          timelineTaskType
        );

        switch (timelineTaskType) {
          case UserTaskType.DISPATCHER:
            return new DispatcherSimpleItem(id, {
              createdAt: parseISODateString(status.created_at),
              title: title,
              user: resolveTaskOwner(task) || null,
              dispatcher: status.dispatcher || null,
              source: status
            });
          case UserTaskType.PATROL:
            return new PatrolSimpleItem(id, {
              createdAt: parseISODateString(status.created_at),
              title: title,
              user: resolveTaskOwner(task) || status.user || null,
              dispatcher: status.dispatcher || null,
              source: status
            });
          case UserTaskType.SERVICE:
            return new ServiceSimpleItem(id, {
              createdAt: parseISODateString(status.created_at),
              title: title,
              source: status
            });
          default:
            return new SimpleItem(id, {
              createdAt: parseISODateString(status.created_at),
              title: title,
              source: status
            });
        }
      }
    }
  }
}

// Specific timeline item types
export class MessageItem extends TimelineItem {
  constructor(id, data = {}) {
    super(id, TimelineItemType.MESSAGE, {
      ...data,
      icon: data.icon || 'mdi-message-outline',
      color: data.color || 'info',
    });

    // Additional MessageItem properties
    this.taskId = data.taskId || null;
    this.taskType = data.taskType || null;
    this.subtitle = data.subtitle || '';
    this.details = data.details || '';
    this.isCurrentUser = data.isCurrentUser || false;
  }

  // Method for creating draft messages
  static createDraft(taskId, taskType, message, user = null) {
    const id = `draft-${Date.now()}-${Math.random()}`;
    return new MessageItem(id, {
      taskId: taskId,
      taskType: taskType,
      subtitle: message,
      message: '', // Empty message for simple comments
      user: user,
      isCurrentUser: store.getters['user/isCurrentUser'](user?.id),
      status: MessageStatus.DRAFT,
      createdAt: new Date(),
      important: false,
      canDelete: true, // Draft messages can be deleted
    });
  }
}

export class UserSimpleItem extends TimelineItem {
  constructor(id, data = {}) {
    super(id, TimelineItemType.USER_SIMPLE, {
      ...data,
      icon: data.icon || 'mdi-account-tie',
      color: data.color || 'primary',
      isCurrentUser: store.getters['user/isCurrentUser'](data.user?.id),
      avatar: true,
    });
  }
}

export class DateItem extends TimelineItem {
  constructor(id, data = {}) {
    super(id, TimelineItemType.DATE, {
      ...data,
      icon: data.icon || 'mdi-calendar',
      color: data.color || 'default',
      canEdit: false, // Date items should not be editable
      canDelete: false, // Date items should not be deletable
    });

    // DateItem specific property for text display
    this.text = data.text || data.title || '';
  }
}

export class ServiceSimpleItem extends TimelineItem {
  constructor(id, data = {}) {
    super(id, TimelineItemType.SERVICE_SIMPLE, {
      ...data,
      icon: data.icon || 'mdi-cog',
      color: data.color || 'secondary',
    });

    this.canEditOnSiteTime = data.canEditOnSiteTime || false;
  }

  updateOnSiteTime(seconds) {
    this.subtitle = serviceDurationSubtitle({
      details: {
        stats: {
          on_site_time: seconds,
        },
      },
    });
  }
}

export class PatrolSimpleItem extends TimelineItem {
  constructor(id, data = {}) {
    super(id, TimelineItemType.PATROL_SIMPLE, {
      ...data,
      icon: data.icon || 'mdi-car',
      color: data.color || 'secondary',
      isCurrentUser: false,
    });
  }
}

export class DispatcherSimpleItem extends TimelineItem {
  constructor(id, data = {}) {
    super(id, TimelineItemType.DISPATCHER_SIMPLE, {
      ...data,
      icon: data.icon || 'mdi-account-tie',
      isCurrentUser: store.getters['user/isCurrentUser'](data.user?.id),
      color: data.color || 'secondary',
    });
  }
}

export class SimpleItem extends TimelineItem {
  constructor(id, data = {}) {
    super(id, TimelineItemType.SIMPLE, {
      ...data,
      icon: data.icon || 'mdi-circle-outline',
      isCurrentUser: true, //store.getters['user/isCurrentUser'](data.user?.id),
      color: data.color || 'default',
    });
  }
}

// AtLocationItem - specialized timeline item for "At Location" status with travel time
export class AtLocationItem extends TimelineItem {
  constructor(id, data = {}) {
    super(id, TimelineItemType.AT_LOCATION, {
      ...data,
      icon: data.icon || 'mdi-map-marker',
      isCurrentUser: false,
      color: data.color || 'info',
      canEditTravelTime: data.canEditTravelTime || false,
      travelTime: data.travelTime || null,
      travelTimeFormatted: data.travelTimeFormatted || null,
      editTravelTimeMethod: data.editTravelTimeMethod || null,
    });

    // Store travel time properties
    this.travelTime = data.travelTime || null;
    this.travelTimeFormatted = data.travelTimeFormatted || null;
    this.canEditTravelTime = data.canEditTravelTime || false;
    this.editTravelTimeMethod = data.editTravelTimeMethod || null;
  }

  // Method to update travel time
  updateTravelTime(seconds) {
    this.travelTime = seconds;
    this.travelTimeFormatted = this.formatTravelTime(seconds);
  }

  // Helper method to format travel time
  formatTravelTime(seconds) {
    return formatDuration(seconds);
  }

  // Factory method to create AtLocationItem with travel time data
  static createWithTravelTime(id, task, status, options = {}) {
    const travelTime = task.details?.stats?.travel_time || null;
    const travelTimeFormatted = travelTime ? AtLocationItem.prototype.formatTravelTime(travelTime) : null;

    return new AtLocationItem(id, {
      title: options.title || 'At Location',
      subtitle: options.subtitle || null,
      createdAt: parseISODateString(status.created_at),
      taskId: task.id,
      taskType: task.task_type,
      user: resolveTaskOwner(task) || null,
      isCurrentUser: store.getters['user/isCurrentUser'](status.user?.id),
      source: status,
      canEdit: false, // AtLocation items are not directly editable (only travel time)
      canEditTravelTime: options.canEditTravelTime || false,
      editTravelTimeMethod: options.editTravelTimeMethod || null,
      travelTime: travelTime,
      travelTimeFormatted: travelTimeFormatted,
      color: options.color || 'info',
      icon: options.icon || 'mdi-map-marker',
      ...options
    });
  }
}

/**
 * #######################################################################
 * Converts an array of task objects into an array of timeline item objects.
 * @param {*} tasks - Array of task objects to convert
 * @param {Function} filterCallback - Optional callback function to filter timeline items (task, status) => boolean
 * @returns {Array} Filtered array of timeline items
 */
export function convertTasksToTimelineItems(tasks, filterCallback = null) {
  if (!tasks) {
    return [];
  }
  const items = [];
  try {
    // Process main task and subtasks (like Swift)
    const allTasks = [];
    if (Array.isArray(tasks)) {
      allTasks.push(...tasks);
      for (const task of allTasks) {
        if (task.subtasks && Array.isArray(task.subtasks)) {
          // Flatten subtasks into the main array
          allTasks.push(...task.subtasks);
        }
      }
    } else {
      allTasks.push(tasks);
    }
    const ids = new Set();

    for (const currentTask of allTasks) {
      appendTaskToTimeline(currentTask, items, ids, filterCallback);
    }
    // Sort by creation date
    items.sort((a, b) => a.createdAt - b.createdAt);

    return items;
  } catch (error) {
    console.error('Error converting task to timeline items:', error);
    return [];
  }
}

// Helper function similar to Swift's Array extension append
function appendTaskToTimeline(task, items, ids, filterCallback = null) {
  if (!task.details) {
    return;
  }

  // Process history items
  if (task.details.history) {
    task.details.history.forEach((status) => {
      if (filterCallback && !filterCallback(task, status)) {
        return; // Skip this status if it doesn't pass the filter
      }
      // Add date item if not already present
      const statusDate = startOfDay(parseISODateString(status.created_at));
      const dateItem = TimelineItem.createDateItem(statusDate);
      if (!ids.has(dateItem.id)) {
        ids.add(dateItem.id);
        items.push(dateItem);
      }

      // Add timeline item for this status
      const timelineItem = TimelineItem.create(task, status);
      if (!ids.has(timelineItem.id)) {
        ids.add(timelineItem.id);
        items.push(timelineItem);
      }
    });
  }

  // Process found items
  if (task.details.found && Array.isArray(task.details.found)) {
    task.details.found.forEach((foundItem) => {
      if (filterCallback && !filterCallback(task, foundItem)) {
        return; // Skip this found item if it doesn't pass the filter
      }
      const foundId = `${task.id}-found-${foundItem.id}`;
      if (!ids.has(foundId)) {
        // Add date item if not already present
        const foundDate = startOfDay(parseISODateString(foundItem.created_at));
        const dateItem = TimelineItem.createDateItem(foundDate);
        if (!ids.has(dateItem.id)) {
          ids.add(dateItem.id);
          items.push(dateItem);
        }

        // Create found timeline item
        const foundTimelineItem = new UserSimpleItem(foundId, {
          taskId: task.id,
          taskType: task.task_type,
          title: 'Osoba w obiekcie',
          user: { id: foundItem.id, name: foundItem.full_name },
          createdAt: parseISODateString(foundItem.created_at),
          icon: 'mdi-eye',
          color: 'warning',
          source: foundItem,
          canEdit: true,
          canDelete: true,
        });

        ids.add(foundId);
        items.push(foundTimelineItem);
      }
    });
  }

  // Process notified items
  if (task.details.notified && Array.isArray(task.details.notified)) {
    task.details.notified.forEach((notifiedItem) => {
      if (filterCallback && !filterCallback(task, notifiedItem)) {
        return // Skip this found item if it doesn't pass the filter
      }
      const notifiedId = `${task.id}-notified-${notifiedItem.id}`;
      if (!ids.has(notifiedId)) {
        // Add date item if not already present
        const notifiedDate = startOfDay(parseISODateString(notifiedItem.created_at));
        const dateItem = TimelineItem.createDateItem(notifiedDate);
        if (!ids.has(dateItem.id)) {
          ids.add(dateItem.id);
          items.push(dateItem);
        }
        // Create notified timeline item
        const notifiedTimelineItem = new UserSimpleItem(notifiedId, {
          taskId: task.id,
          taskType: task.task_type,
          title: 'Powiadomiono',
          user: { id: notifiedItem.id, name: notifiedItem.full_name },
          createdAt: parseISODateString(notifiedItem.created_at),
          icon: 'mdi-bell',
          color: 'info',
          source: notifiedItem,
          canEdit: true,
          canDelete: true,
        });

        ids.add(notifiedId);
        items.push(notifiedTimelineItem);
      }
    });
  }

  // Add closed item for root tasks (like Swift logic).
  // The synthetic close entry derived from closed_at is a fallback only:
  // if an explicit CLOSE already exists in history it takes precedence, so we
  // skip the synthetic one to avoid duplicate close entries in the timeline.
  const hasCloseInHistory = Array.isArray(task.details.history)
    && task.details.history.some((status) => status.label_id === LabelId.CLOSE);
  if (isRootTask(task) && isTaskClosed(task) && !hasCloseInHistory) {
    const timelineTaskType = resolveTimelineTaskType(task);
    const closedPayload = {
      taskId: task.id,
      taskType: timelineTaskType,
      createdAt: parseISODateString(task.closed_at),
      title: Labels.descriptionInContext(LabelId.CLOSE, timelineTaskType),
      subtitle: timelineTaskType === UserTaskType.SERVICE ? serviceDurationSubtitle(task) : '',
    };
    if (timelineTaskType === UserTaskType.SERVICE) {
      closedPayload.icon = 'mdi-wrench';
      closedPayload.canEdit = true;
      closedPayload.canEditOnSiteTime = true;
    }
    const closedItem = timelineTaskType === UserTaskType.SERVICE
      ? new ServiceSimpleItem(`${task.id}-closed`, closedPayload)
      : new DispatcherSimpleItem(`${task.id}-closed`, closedPayload);
    items.push(closedItem);
  }
}

/**
 * Helper filter functions that can be used as callbacks
 */
export const TimelineFilters = {
  // Filter by timeline item types
  byItemTypes: (types) => (item) => types.includes(item.type),

  // Filter by task types
  byTaskTypes: (taskTypes) => (item) =>
    !item.taskType || taskTypes.includes(item.taskType),

  // Filter by user IDs
  byUserIds: (userIds) => (item) =>
    !item.user || userIds.includes(item.user.id),

  // Filter by date range
  byDateRange: (dateFrom, dateTo) => (item) => {
    if (!item.createdAt) return true;
    const created = item.createdAt;
    return (!dateFrom || created >= dateFrom) && (!dateTo || created <= dateTo);
  },

  // Filter by label IDs
  byLabelIds: (labelIds) => (item) =>
    !item.source || labelIds.includes(item.source.label_id),

  // Filter to show only current user items
  currentUserOnly: () => (item) => item.isCurrentUser,

  // Filter to show only important items
  importantOnly: () => (item) => item.important,

  // Filter to exclude date items
  excludeDateTime: () => (item) => item.type !== TimelineItemType.DATE,

  // Combine multiple filters with AND logic
  and: (...filters) => (item) => filters.every((filter) => filter(item)),

  // Combine multiple filters with OR logic
  or: (...filters) => (item) => filters.some((filter) => filter(item)),

  // Negate a filter
  not: (filter) => (item) => !filter(item),
};

/**
 * Filter callbacks for convertTasksToTimelineItems
 * These operate on (task, status/item) level during generation
 */
export const TimelineFilterCallbacks = {
  summaryRelevantItems: (task, item) => {
    // Show only items relevant for summary view
    const isFinalStatus = item && Labels.isFinal(item.label_id);
    const isComment = item && item.label_id === LabelId.COMMENT;
    const atLocation = item && item.label_id === LabelId.AT_LOCATION;
    return isFinalStatus || isComment || atLocation; // Show final statuses, comments, and at_location items
  },
};

// Method for sending messages)
export function sendMessage(task, message, user = null) {
  if (!task || !message.trim()) {
    return null;
  }

  // Create draft message item
  const draftItem = MessageItem.createDraft(
    task.id,
    task.task_type,
    message,
    user
  );

  // TODO: Add API call to send message
  // This would be similar to Swift's UserTaskClient().sendMessage()

  return draftItem;
}
